import Dialog from "@components/Dialog"
import { updateNodeExpression } from "@features/builder/graph/contextMenu/ContextNodeMenu/duplicateNodes"
import { oldToNewLabelMap } from "@features/builder/graph/contextMenu/RenameDialog/oldToNewLabelMap"
import {
  FormValues,
  getRenameDialogSchema,
} from "@features/builder/graph/contextMenu/RenameDialog/schema"
import { SearchAndReplace } from "@features/builder/graph/contextMenu/RenameDialog/SearchAndReplace"
import { nodesToDuplicateAtom } from "@features/builder/graph/contextMenu/useCopyPaste"
import { AppNode, NodeType } from "@features/builder/graph/types"
import { LabelField } from "@features/builder/NodeDrawer/NodeForm/LabelField"
import { LagLabelField } from "@features/builder/NodeDrawer/NodeForm/LagLabelField"
import { yupResolver } from "@hookform/resolvers/yup"
import { Button, DialogActions, List, ListItem } from "@mui/material"
import { createLagNodeLabel } from "@utils/node"
import { splitLagNodeLabel } from "@utils/node/labelValidation"
import { useReactFlow } from "@xyflow/react"
import { useAtom } from "jotai"
import { useEffect, useMemo } from "react"
import { FormProvider, useForm } from "react-hook-form"

export function RenameDialog() {
  const [nodesToDuplicate, setNodesToDuplicate] = useAtom(nodesToDuplicateAtom)
  const { getNodes, getNode, setNodes, addNodes } = useReactFlow<AppNode>()

  const form = useForm<FormValues>({
    resolver: yupResolver(getRenameDialogSchema(getNodes())),
    mode: "onChange",
  })

  const sorted = useMemo(
    () => (nodesToDuplicate.nodes.length > 0 ? sortNodes(nodesToDuplicate.nodes, getNodes()) : []),
    [nodesToDuplicate.nodes, getNodes],
  )

  useEffect(() => {
    if (sorted && sorted.length > 0) {
      form.reset(nodesToFormData(sorted))
      form.trigger()
    }
  }, [form, sorted])

  const handleClose = () => {
    setNodesToDuplicate({ nodes: [], oldToNewIdMap: new Map() })
  }

  const handleSubmit = (values: FormValues) => {
    const updatedNodes = sorted?.map((node, i) => {
      const newLabel = values.labels[i]?.label ?? ""

      let updatedNode = structuredClone(node)

      // Update all label references
      updatedNode.data.label = newLabel

      if (updatedNode.type === NodeType.Deterministic || updatedNode.type === NodeType.Lag) {
        const labelMap = oldToNewLabelMap(values, nodesToDuplicate.oldToNewIdMap, getNode)
        updatedNode = updateNodeExpression(updatedNode, labelMap)
      }

      // Update meta references based on node type
      if (updatedNode.type !== NodeType.Group) updatedNode.data.metaNode.label = newLabel
      else updatedNode.data.metaGroup.label = newLabel

      // Recreate label for Lag node
      if (updatedNode.type === NodeType.Lag) {
        const lag = values.labels[i]?.lagValue ?? 1
        const lagLabel = createLagNodeLabel(updatedNode.data.label, lag)
        updatedNode.data.metaNode.label = lagLabel
        updatedNode.data.label = lagLabel
      }

      return updatedNode
    })

    // Deselect all old nodes, only new nodes should be selected
    setNodes((nodes) => nodes.map((node) => ({ ...node, selected: false })))

    addNodes(updatedNodes ?? [])
    setNodesToDuplicate({ nodes: [], oldToNewIdMap: new Map() })
  }

  return (
    <Dialog
      actionsComponent={
        <DialogActions sx={{ p: "0 24px 20px 24px" }}>
          <Button onClick={handleClose} color="error" variant="contained">
            Cancel
          </Button>
          <Button form="rename-dialog-form" type="submit" color="primary" variant="contained">
            Save
          </Button>
        </DialogActions>
      }
      open
      title="Rename duplicated nodes"
      onClose={handleClose}
      fullWidth
    >
      <FormProvider {...form}>
        <form onSubmit={form.handleSubmit(handleSubmit)} id="rename-dialog-form">
          <SearchAndReplace />
          <List>
            {sorted?.map((node, i) =>
              node.type === NodeType.Lag ? (
                <ListItem key={node.id}>
                  <LagLabelField
                    control={form.control}
                    name={`labels.${i}.label`}
                    lag={splitLagNodeLabel(node.data.label).lagValue}
                  />
                </ListItem>
              ) : (
                <ListItem key={node.id}>
                  <LabelField control={form.control} name={`labels.${i}.label`} />
                </ListItem>
              ),
            )}
          </List>
        </form>
      </FormProvider>
    </Dialog>
  )
}

const sortNodes = (nodesToDuplicate: AppNode[], allNodes: AppNode[]) => {
  const schema = getRenameDialogSchema(allNodes)
  const tempFormData = nodesToFormData(nodesToDuplicate)
  const sortedNodes: AppNode[] = []

  for (const [i, node] of nodesToDuplicate.entries()) {
    try {
      schema.validateSyncAt(`labels.${i}.label`, tempFormData, {
        context: tempFormData, // Pass full form data as context
      })
      sortedNodes.push(node)
    } catch {
      sortedNodes.unshift(node)
    }
  }

  return sortedNodes
}

const nodesToFormData = (nodes: AppNode[]) => {
  return {
    labels: nodes.map((node) => {
      let label = node.data.label
      // stet it to undefined so we can check in schema if we are dealing with lag node without having access to it's type
      let lagValue = undefined
      if (node.type === NodeType.Lag) {
        label = splitLagNodeLabel(node.data.label).label
        lagValue = splitLagNodeLabel(node.data.label).lagValue
      }

      return {
        label,
        lagValue,
        id: node.id,
        type: node.type,
      }
    }),
  }
}
